/*
 * File: PaymentSummary.tsx
 * Component: PaymentSummary
 * Description: Displays a summary of payment information including the total amount,
 * unallocated funds, and the projected student balance after payment.
 */
import { useEffect, useState } from 'react';

import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useFormContext } from 'react-hook-form';

import { useGetStudentLedger } from 'api/hooks/private/users/students/GetStudentLedger';

import { formatCents } from 'utils/formatCurrency';

import FinanceSummaryItem from '../../../components/FinanceSummaryItem';
import { PaymentFormValues } from '../PaymentSchema';

function PaymentSummary() {
  // projected student balance after payment
  const [newBalance, setNewBalance] = useState(0);
  const {
    watch,
    formState: { errors },
  } = useFormContext<PaymentFormValues>();

  // fetch student's ledger data for balance calculation
  const { data: studentLedger } = useGetStudentLedger({
    variables: {
      id: watch('student_user_id'),
      showVoided: true,
    },
    initialData: [],
  });

  // get payment amount and allocations from form
  const paymentTotal = watch('amount') ?? 0;
  const paymentAllocations = watch('allocations') ?? [];

  // calculate the total payment amount allocated
  const allocatedTotal = paymentAllocations.reduce((total: number, allocation: PaymentFormValues['allocations'][0]) => {
    return total + (allocation?.amount ?? 0);
  }, 0);

  // calculate remaining unallocated amount
  const unallocatedTotal = paymentTotal - allocatedTotal;

  useEffect(() => {
    // wait until the ledger is fetched
    if (!studentLedger) return;

    // calculate new account balance by:
    // 1. getting current balance excluding this payment
    // 2. subtracting the payment amount
    const currentPaymentId = watch('id');
    const prevPaymentAmount = Number(studentLedger?.find((entry) => entry.id === currentPaymentId)?.amount ?? 0);
    const currentBalance = Number(studentLedger[studentLedger.length - 1]?.balance ?? 0);
    setNewBalance(currentBalance + prevPaymentAmount - paymentTotal);
  }, [paymentTotal, studentLedger, watch]);

  return (
    <Grid container rowSpacing={0} columnSpacing={3}>
      <FinanceSummaryItem title="Payment Amount" value={formatCents(paymentTotal)} error={!!errors['amount']} />
      <FinanceSummaryItem title="Allocated Total" value={formatCents(allocatedTotal)} />
      <FinanceSummaryItem title="Unallocated" value={formatCents(unallocatedTotal)} />
      <Grid size={12} py={1}>
        <Divider />
      </Grid>
      <FinanceSummaryItem title="New Student Balance" value={formatCents(newBalance)} />
    </Grid>
  );
}

export default PaymentSummary;
